import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Typography } from "antd";

import type { JsonValue } from "../types";
import { validateUserSafeUpdate } from "../utils/user-update-validation";
import { SafeJsonEditor } from "../safe-json-editor";

interface Props {
  mode: "safe" | "advanced";
  visible: boolean;
  title: string;
  initialValue: JsonValue;
  canResetSchema: boolean;
  canEditStructure: boolean;
  onClose: () => void;
  onSave: (payload: { value: JsonValue; resetSchema: boolean }) => Promise<void>;
}

const inferType = (value: JsonValue): "string" | "number" | "boolean" | "null" | "object" | "array" => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") return "object";
  if (typeof value === "string") return "string";
  if (typeof value === "number") return "number";
  return "boolean";
};

const JsonEditorModal: React.FC<Props> = ({
  mode,
  visible,
  title,
  initialValue,
  canResetSchema,
  canEditStructure,
  onClose,
  onSave,
}) => {
  const [jsonText, setJsonText] = useState(() => JSON.stringify(initialValue, null, 2));
  const [nodeType, setNodeType] = useState<"string" | "number" | "boolean" | "null" | "object" | "array">(() => inferType(initialValue));
  const [primitiveValue, setPrimitiveValue] = useState<string | number | boolean>("");
  const [safeValue, setSafeValue] = useState<JsonValue>(initialValue);
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetSchema, setResetSchema] = useState(false);

  const isPrimitive = nodeType === "string" || nodeType === "number" || nodeType === "boolean" || nodeType === "null";

  const helperText = useMemo(() => {
    if (mode === "advanced") {
      return "Advanced mode allows full JSON paste. Valid JSON is mandatory.";
    }

    return "Safe mode provides a searchable tree and detail panel for editing values, with add/remove restrictions enforced for non-admins.";
  }, [mode]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const inferredType = inferType(initialValue);
    setJsonText(JSON.stringify(initialValue, null, 2));
    setNodeType(inferredType);
    setSafeValue(initialValue);
    setErrorMessage("");
    setResetSchema(false);

    if (inferredType === "string" || inferredType === "number" || inferredType === "boolean") {
      setPrimitiveValue(initialValue as string | number | boolean);
    } else {
      setPrimitiveValue("");
    }
  }, [initialValue, visible]);

  const getBuiltPrimitive = (): JsonValue => {
    if (nodeType === "null") return null;
    if (nodeType === "boolean") return Boolean(primitiveValue);
    if (nodeType === "number") return Number(primitiveValue);
    return String(primitiveValue);
  };

  const handleSave = async () => {
    try {
      setErrorMessage("");
      setSaving(true);

      let value: JsonValue;
      if (mode === "safe") {
        value = safeValue;
      } else if (isPrimitive) {
        value = getBuiltPrimitive();
      } else {
        value = JSON.parse(jsonText) as JsonValue;
      }

      if (inferType(value) !== nodeType) {
        throw new Error("Root JSON type cannot be changed");
      }

      if (mode === "safe" && !canEditStructure) {
        const validationError = validateUserSafeUpdate(initialValue, value);
        if (validationError) {
          throw new Error(validationError);
        }
      }

      await onSave({ value, resetSchema: canResetSchema && resetSchema });
      onClose();
    } catch (error) {
      if (error instanceof SyntaxError) {
        setErrorMessage("Invalid JSON payload");
      } else if (error instanceof Error && error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Failed to save JSON");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      title={title}
      centered
      width={mode === "safe" ? "calc(100vw - 24px)" : "min(900px, calc(100vw - 32px))"}
      bodyStyle={{
        maxHeight: mode === "safe" ? "calc(100vh - 130px)" : "calc(100vh - 180px)",
        padding: mode === "safe" ? "12px 16px" : undefined,
        overflowX: "hidden",
        overflowY: mode === "safe" ? "hidden" : "auto",
      }}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save
        </Button>,
      ]}
      destroyOnClose
    >
      {errorMessage ? <Alert showIcon type="error" message={errorMessage} style={{ marginBottom: 12 }} /> : null}
      {mode !== "safe" ? <Typography.Paragraph type="secondary">{helperText}</Typography.Paragraph> : null}

      <Form layout="vertical">
        {mode === "safe" ? (
          <Form.Item style={{ minWidth: 0, marginBottom: 0 }}>
            <SafeJsonEditor
              value={safeValue}
              onChange={setSafeValue}
              canEditStructure={canEditStructure}
              height="calc(100vh - 158px)"
            />
          </Form.Item>
        ) : isPrimitive ? (
          <Form.Item label="Value">
            {nodeType === "number" ? (
              <InputNumber value={typeof primitiveValue === "number" ? primitiveValue : 0} onChange={(value) => setPrimitiveValue(value ?? 0)} />
            ) : null}
            {nodeType === "string" ? (
              <Input value={typeof primitiveValue === "string" ? primitiveValue : ""} onChange={(event) => setPrimitiveValue(event.target.value)} />
            ) : null}
            {nodeType === "boolean" ? (
              <Select value={Boolean(primitiveValue)} onChange={(value) => setPrimitiveValue(value)}>
                <Select.Option value={true}>true</Select.Option>
                <Select.Option value={false}>false</Select.Option>
              </Select>
            ) : null}
            {nodeType === "null" ? <Typography.Text>null</Typography.Text> : null}
          </Form.Item>
        ) : (
          <Form.Item label="JSON" style={{ minWidth: 0 }}>
            <Input.TextArea
              rows={18}
              spellCheck={false}
              style={{ maxWidth: "100%" }}
              value={jsonText}
              onChange={(event) => setJsonText(event.target.value)}
            />
          </Form.Item>
        )}

        {canResetSchema ? (
          <Form.Item>
            <Space>
              <input id="reset-schema" type="checkbox" checked={resetSchema} onChange={(event) => setResetSchema(event.target.checked)} />
              <label htmlFor="reset-schema">Reset schema from this JSON</label>
            </Space>
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
};

export default JsonEditorModal;
